// P5.2 — Profile completion bar (Pepe spec).
// Polls /api/users/me/completion and renders a friendly, non-blocking
// progress card. Used in two surfaces:
//   • /app/settings page → ALWAYS visible (full-width header).
//   • /app dashboard      → ONLY when percentage < 80 (gentle reminder).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { ArrowRight, Sparkles } from "lucide-react";

const ProfileCompletionBar = ({ variant = "settings" }) => {
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/users/me/completion").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  // Dashboard variant: hide once profile is 80%+ complete (Pepe's rule
  // — don't pester users who are already mostly done).
  if (variant === "dashboard" && (data.percentage ?? 0) >= 80) return null;

  const pct = Math.max(0, Math.min(100, data.percentage || 0));
  const hint = data.recommended_next?.hint_key;
  // Resolve the hint via i18n; fall back to a generic progressive note.
  const hintText = hint ? t(hint) : t("settings.completion.progressiveNote");

  return (
    <div
      className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-4 sm:p-5"
      data-testid={`profile-completion-${variant}`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="w-10 h-10 rounded-xl bg-white/80 border border-emerald-200 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <h3 className="text-sm sm:text-base font-semibold text-emerald-900">
              {t("settings.completion.title")}
            </h3>
            <span className="text-sm tabular-nums font-semibold text-emerald-900" data-testid={`profile-completion-${variant}-pct`}>
              {pct}% {t("settings.completion.completedLabel")}
            </span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-white/80 overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${pct}%` }}
              data-testid={`profile-completion-${variant}-bar`}
            />
          </div>
          {hintText && (
            <p className="mt-2 text-xs sm:text-sm text-emerald-900/80 leading-relaxed">
              {hintText}
            </p>
          )}
          {variant === "dashboard" && (
            <Link
              to="/app/settings"
              className="inline-flex items-center gap-1.5 mt-3 text-xs sm:text-sm font-medium text-emerald-700 hover:text-emerald-800"
              data-testid="profile-completion-dashboard-link"
            >
              {t("settings.completion.goToSettings")} <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionBar;
